import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PiXFill } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import {
  coerceFilterValues,
} from '#/modules/commonComponents/table/columnFilterUtils';

function resolveFilterDisplayText(field, rawValue) {
  const values = coerceFilterValues(rawValue);

  if (values.length === 0) {
    return '';
  }

  if (field.resolveDisplay) {
    const labels = field.resolveDisplay(values);
    const hasUnresolved = labels.some((label, index) => label === values[index]);

    if (hasUnresolved) {
      return `已選 ${values.length} 項`;
    }

    return labels.join(', ');
  }

  return values.join(', ');
}

function ListFilterStatus({
  listQuery,
  filterFields = [],
  sortFieldLabels = {},
  onFilterChange,
  onKeywordClear,
  onSortRemove,
}) {
  const chips = useMemo(() => {
    const items = [];

    (listQuery.sort ?? []).forEach((sortItem, index) => {
      const sortLabel = sortFieldLabels[sortItem.id] ?? sortItem.id;
      items.push({
        id: `sort-${sortItem.id}-${index}`,
        type: 'sort',
        text: `${index + 1}:${sortLabel} ${sortItem.desc ? '↓' : '↑'}`,
        onRemove: () => onSortRemove?.(index),
      });
    });

    if (listQuery.keyword) {
      items.push({
        id: 'keyword',
        type: 'filter',
        text: `搜尋: ${listQuery.keyword}`,
        onRemove: () => onKeywordClear?.(),
      });
    }

    filterFields.forEach((field) => {
      const rawValue = listQuery.filters?.[field.key];
      const displayText = resolveFilterDisplayText(field, rawValue);

      if (!displayText) {
        return;
      }

      items.push({
        id: `filter-${field.key}`,
        type: 'filter',
        text: `${field.label}: ${displayText}`,
        onRemove: () => onFilterChange?.(field.key, undefined),
      });
    });

    return items;
  }, [
    filterFields,
    listQuery.filters,
    listQuery.keyword,
    listQuery.sort,
    onFilterChange,
    onKeywordClear,
    onSortRemove,
    sortFieldLabels,
  ]);

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="flex min-h-7 min-w-0 flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <span
          key={chip.id}
          className={cn(
            'list-filter-chip inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs',
            chip.type === 'sort'
              ? 'bg-green-200 text-green-900'
              : 'bg-accent-200 text-accent-900',
          )}
        >
          <span>{chip.text}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-5 text-inherit hover:bg-black/5"
            onClick={chip.onRemove}
            aria-label={`清除 ${chip.text}`}
          >
            <PiXFill className="size-3" aria-hidden />
          </Button>
        </span>
      ))}
    </div>
  );
}

ListFilterStatus.propTypes = {
  listQuery: PropTypes.shape({
    keyword: PropTypes.string,
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        desc: PropTypes.bool,
      }),
    ),
    filters: PropTypes.object,
  }).isRequired,
  filterFields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      resolveDisplay: PropTypes.func,
    }),
  ),
  sortFieldLabels: PropTypes.object,
  onFilterChange: PropTypes.func,
  onKeywordClear: PropTypes.func,
  onSortRemove: PropTypes.func,
};

export default ListFilterStatus;
